import { formatDecimalFr } from '@cf/shared';
import { useTranslation } from 'react-i18next';

export interface PagerProps {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}

/** Page numbers to show: first, last, current ± 1, with gaps ("…") in between. */
export function pageList(current: number, pages: number): (number | 'gap')[] {
  const keep = new Set([1, pages, current - 1, current, current + 1]);
  const out: (number | 'gap')[] = [];
  for (let p = 1; p <= pages; p++) {
    if (keep.has(p)) out.push(p);
    else if (out.at(-1) !== 'gap') out.push('gap');
  }
  return out;
}

const base =
  'inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-lg px-2.5 text-[13px] font-medium transition-colors';
const btn = `${base} bg-surface text-stone-700 shadow-xs ring-1 ring-inset ring-stone-300 hover:bg-stone-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-surface`;
const currentBtn = `${base} bg-copper-600 text-on-fill shadow-xs ring-1 ring-inset ring-copper-700/20`;

/**
 * Shown only when the list does not fit on one page: small lists stay a plain table.
 * "26–50 sur 312" + ‹ Précédent · 1 2 3 … 13 · Suivant ›.
 */
export function Pager({ page, pageSize, total, onPage }: PagerProps) {
  const { t } = useTranslation();
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  const current = Math.min(Math.max(page, 1), pages);
  const n = (v: number) => formatDecimalFr(String(v), 0);
  return (
    <nav
      aria-label={t('common.pager.label')}
      className="flex flex-wrap items-center justify-between gap-3 px-1 text-sm text-stone-600"
    >
      <span className="tabular-nums">
        {t('common.pager.range', {
          from: n((current - 1) * pageSize + 1),
          to: n(Math.min(total, current * pageSize)),
          total: n(total),
        })}
      </span>
      <span className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          className={btn}
          disabled={current <= 1}
          onClick={() => onPage(current - 1)}
        >
          <span aria-hidden className="inline-block rtl:rotate-180">
            ‹
          </span>
          {t('common.pager.prev')}
        </button>
        {pageList(current, pages).map((p, i) =>
          p === 'gap' ? (
            <span key={`gap${i}`} aria-hidden className="px-1 text-stone-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              aria-current={p === current ? 'page' : undefined}
              aria-label={t('common.pager.goTo', { page: p })}
              onClick={() => onPage(p)}
              className={`${p === current ? currentBtn : btn} tabular-nums`}
            >
              {n(p)}
            </button>
          ),
        )}
        <button
          type="button"
          className={btn}
          disabled={current >= pages}
          onClick={() => onPage(current + 1)}
        >
          {t('common.pager.next')}
          <span aria-hidden className="inline-block rtl:rotate-180">
            ›
          </span>
        </button>
      </span>
    </nav>
  );
}
